import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  ColorSchemeProvider,
  DEFAULT_THEME,
  MantineProvider,
  useMantineTheme,
} from "@mantine/core";
import brand from "./brand";
import { phoneComponents } from "./phoneTheme";
import { buildPalette, contrastRatio, mixHex, normalizeHex } from "./colorPalette";

// The look each browser remembers (spec D6: per browser, the crmuser table is not altered).
// Every saved value is checked on the way in, so a stale or hand-edited entry falls back to the
// default instead of a white screen: Mantine throws on an unknown primaryColor.
const SCHEME_KEY = "FDI_COLOR_SCHEME";
const PRIMARY_KEY = "FDI_PRIMARY";
const CUSTOM_HEX_KEY = "FDI_CUSTOM_HEX";

const DEFAULT_SCHEME = "light"; // spec C4: light and the brand colour, not the OS preference
const DEFAULT_PRIMARY = "brand";
const CUSTOM_KEY = "custom";
// The theme colours of the user menu (spec C3): the brand colour first, then Mantine's own.
const PRESET_KEYS = ["brand", "yellow", "blue", "teal", "green", "violet", "pink"];
const PRESET_LABELS = {
  yellow: "Yellow",
  blue: "Blue",
  teal: "Teal",
  green: "Green",
  violet: "Violet",
  pink: "Pink",
};

// Always light: the sign-in page (and "/", which redirects there) and the printable proposal,
// whose A4 sheets are white paper (spec C7). public/index.html paints with the same rule.
const FORCED_LIGHT = /^\/(login|proposalpdf)?(\/|$)/i;

const FONT_FAMILY = "'Poppins', sans-serif";
const BRAND_PALETTE =
  Array.isArray(brand.palette) && brand.palette.length === 10
    ? brand.palette
    : DEFAULT_THEME.colors.blue;

const WHITE = "#ffffff";
const SIDEBAR_TEXT_ALPHA = 0.85;
const MIN_CONTRAST = 4.5; // WCAG AA for body text

const readStored = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // storage blocked (private window, policy): the defaults apply
  }
};

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage blocked: the choice lasts until the page reloads
  }
};

function readSavedAppearance() {
  const colorScheme = readStored(SCHEME_KEY) === "dark" ? "dark" : DEFAULT_SCHEME;
  const customHex = normalizeHex(readStored(CUSTOM_HEX_KEY));
  const saved = readStored(PRIMARY_KEY);
  let primaryKey = DEFAULT_PRIMARY;
  if (saved === CUSTOM_KEY && customHex) primaryKey = CUSTOM_KEY;
  else if (PRESET_KEYS.includes(saved)) primaryKey = saved;
  return { colorScheme, primaryKey, customHex };
}

const pathnameOf = (router) => router?.state?.location?.pathname ?? window.location.pathname;

// The provider sits outside RouterProvider, so it follows the data router's own store (every
// navigation, back/forward included) instead of useLocation. Without a router: back/forward only.
function useCurrentPathname(router) {
  const [pathname, setPathname] = useState(() => pathnameOf(router));
  useEffect(() => {
    setPathname(pathnameOf(router));
    if (router?.subscribe) {
      return router.subscribe((state) => setPathname(state.location.pathname));
    }
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [router]);
  return pathname;
}

const paletteOf = (theme) => theme.colors[theme.primaryColor] || theme.colors.brand || BRAND_PALETTE;

// Mantine uses ONE primaryShade for every colour. Its dark default, 8, keeps the stock colours
// readable (red Delete, green Save, blue buttons with white text), but FDI navy's shade 8 is the
// navy itself and vanishes on the dark surfaces. So in dark mode the brand palette is lifted four
// shades (its 8 becomes today's 4) and a custom palette two (its 8 becomes the picked colour).
const liftPalette = (palette, steps) => palette.map((_, i) => palette[Math.max(0, i - steps)]);

// How well `text`, drawn at the sidebar's 85 % strength, reads on every stop of the gradient.
const sidebarReadability = (text, stops) =>
  Math.min(...stops.map((stop) => contrastRatio(mixHex(stop, text, SIDEBAR_TEXT_ALPHA), stop)));

/** The page behind the cards: a faint tint of the theme colour, never grey; dark[8] in dark. */
export function getPageBackground(theme) {
  if (theme.colorScheme === "dark") return theme.colors.dark[8];
  return mixHex(WHITE, paletteOf(theme)[0], 0.4);
}

/** Card, header and text tokens of the refreshed look (spec §2 and C5). */
export function getSurfaceColors(theme) {
  const dark = theme.colorScheme === "dark";
  return {
    page: getPageBackground(theme),
    card: dark ? theme.colors.dark[7] : theme.white,
    border: dark ? theme.colors.dark[5] : theme.colors.gray[2],
    text: dark ? theme.colors.dark[0] : theme.colors.dark[9],
    dimmed: dark ? theme.colors.dark[2] : theme.colors.gray[6],
  };
}

/**
 * The sidebar (and phone drawer) colours for the current theme. Light: a gradient of the theme
 * colour's shades 8 -> 9. Its text is white, or dark ink when white would not reach WCAG 4.5
 * (yellow, a pale custom pick); a mid tone that reads with neither (teal, green) is deepened
 * until white does. Dark: dark[9] with the theme colour only on the active item.
 */
export function getSidebarColors(theme) {
  const p = paletteOf(theme);
  const { rgba } = theme.fn;

  if (theme.colorScheme === "dark") {
    const bg = theme.colors.dark[9];
    // FDI navy is so deep that 45 % of it vanishes on dark[9]: a lighter shade keeps the pill seen.
    const tooDeep = contrastRatio(mixHex(bg, p[8], 0.45), bg) < 1.25;
    return {
      background: bg,
      top: bg,
      bottom: bg,
      text: rgba(theme.colors.dark[0], SIDEBAR_TEXT_ALPHA),
      textStrong: theme.colors.dark[0],
      hover: rgba(WHITE, 0.05),
      active: rgba(tooDeep ? p[6] : p[8], 0.45),
      bar: p[6],
      divider: rgba(WHITE, 0.07),
      caption: rgba(theme.colors.dark[0], 0.5),
      focus: p[6],
      thumb: rgba(WHITE, 0.16),
      thumbHover: rgba(WHITE, 0.28),
      onLight: false,
    };
  }

  let top = p[8];
  let bottom = p[9];
  const ink = theme.colors.dark[9];
  let text = WHITE;
  if (sidebarReadability(WHITE, [top, bottom]) < MIN_CONTRAST) {
    if (sidebarReadability(ink, [top, bottom]) >= MIN_CONTRAST) {
      text = ink;
    } else {
      for (let step = 1; step < 20; step += 1) {
        top = mixHex(p[8], "#000000", step * 0.05);
        bottom = mixHex(p[9], "#000000", step * 0.05);
        if (sidebarReadability(WHITE, [top, bottom]) >= MIN_CONTRAST) break;
      }
    }
  }

  const onLight = text !== WHITE;
  const tone = onLight ? "#000000" : WHITE; // the overlays darken a light sidebar, lighten a deep one
  return {
    background: `linear-gradient(180deg, ${top} 0%, ${bottom} 100%)`,
    top,
    bottom,
    text: rgba(text, SIDEBAR_TEXT_ALPHA),
    textStrong: text,
    hover: rgba(tone, onLight ? 0.06 : 0.08),
    active: rgba(tone, onLight ? 0.1 : 0.14),
    bar: onLight ? text : p[2],
    divider: rgba(tone, onLight ? 0.1 : 0.12),
    caption: rgba(text, 0.55),
    focus: onLight ? text : p[2],
    thumb: rgba(tone, onLight ? 0.25 : 0.3),
    thumbHover: rgba(tone, onLight ? 0.4 : 0.45),
    onLight,
  };
}

const themeComponents = {
  ...phoneComponents,
  // The softer card corner of the refreshed look (spec §2); inputs take defaultRadius "md".
  Card: { defaultProps: { radius: "lg" } },
};

// Mantine rings the keyboard focus with shade 7 in dark mode; shade 8 of the (lifted, see
// liftPalette) palette is the filled colour, which reads on the dark surfaces. Light is unchanged.
const focusRingStyles = {
  styles: (theme) => ({
    outlineOffset: "0.125rem",
    outline: `0.125rem solid ${theme.colors[theme.primaryColor][theme.colorScheme === "dark" ? 8 : 5]}`,
  }),
};

// Replaces the old <Global> that pinned the body to #ffffff: that also kept it white in dark mode.
const globalStyles = (theme) => ({
  body: {
    backgroundColor: getPageBackground(theme),
    color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.dark[9],
  },
  "::selection": {
    backgroundColor: theme.fn.rgba(paletteOf(theme)[theme.colorScheme === "dark" ? 6 : 5], 0.3),
  },
});

// Things outside React's tree that follow the theme: <html data-color-scheme> (the SweetAlert2
// dark CSS keys on it) and the browser bar colour. The pre-paint colours index.html put on <html>
// are dropped here, once Mantine's global styles exist, or they would pin the old scheme.
function DocumentTheme() {
  const theme = useMantineTheme();
  const { colorScheme } = theme;
  const sidebarTop = getSidebarColors(theme).top;

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-color-scheme", colorScheme);
    root.style.removeProperty("background-color");
    root.style.removeProperty("color-scheme");
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", sidebarTop);
  }, [colorScheme, sidebarTop]);

  return null;
}

const AppearanceContext = createContext(null);

/**
 * The whole Mantine theme of the CRM: colour scheme, theme colour (a preset key or "custom"),
 * Poppins and the phone rules. Pass the data router so /login and /proposalpdf stay light.
 */
export function AppearanceProvider({ children, router }) {
  const [saved] = useState(readSavedAppearance);
  const [preferredScheme, setPreferredScheme] = useState(saved.colorScheme);
  const [primaryKey, setPrimaryKeyState] = useState(saved.primaryKey);
  const [customHex, setCustomHexState] = useState(saved.customHex);

  const pathname = useCurrentPathname(router);
  const forcedLight = FORCED_LIGHT.test(pathname);
  const colorScheme = forcedLight ? "light" : preferredScheme;

  // Another tab changed the look: follow it, through the same checks as a fresh load.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key && ![SCHEME_KEY, PRIMARY_KEY, CUSTOM_HEX_KEY].includes(event.key)) return;
      const next = readSavedAppearance();
      setPreferredScheme(next.colorScheme);
      setPrimaryKeyState(next.primaryKey);
      setCustomHexState(next.customHex);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setColorScheme = useCallback((value) => {
    const next = value === "dark" ? "dark" : "light";
    setPreferredScheme(next);
    writeStored(SCHEME_KEY, next);
  }, []);

  // Mantine's signature: an explicit scheme, or no argument to flip the saved one.
  const toggleColorScheme = useCallback(
    (value) => {
      if (value === "dark" || value === "light") setColorScheme(value);
      else setColorScheme(preferredScheme === "dark" ? "light" : "dark");
    },
    [preferredScheme, setColorScheme],
  );

  /** A preset key, or "custom" once a custom colour exists. Returns false when refused. */
  const setPrimaryKey = useCallback(
    (key) => {
      const allowed = key === CUSTOM_KEY ? Boolean(customHex) : PRESET_KEYS.includes(key);
      if (!allowed) return false;
      setPrimaryKeyState(key);
      writeStored(PRIMARY_KEY, key);
      return true;
    },
    [customHex],
  );

  /**
   * Saves a 3- or 6-digit hex as the custom colour and, unless { select: false }, makes it the
   * theme colour at once. Returns false (nothing changes) for anything that is not such a hex.
   */
  const setCustomHex = useCallback((value, { select = true } = {}) => {
    const hex = normalizeHex(value);
    if (!hex) return false;
    setCustomHexState(hex);
    writeStored(CUSTOM_HEX_KEY, hex);
    if (select) {
      setPrimaryKeyState(CUSTOM_KEY);
      writeStored(PRIMARY_KEY, CUSTOM_KEY);
    }
    return true;
  }, []);

  const customPalette = useMemo(() => (customHex ? buildPalette(customHex) : null), [customHex]);
  let primaryColor = DEFAULT_PRIMARY;
  if (primaryKey === CUSTOM_KEY && customPalette) primaryColor = CUSTOM_KEY;
  else if (PRESET_KEYS.includes(primaryKey)) primaryColor = primaryKey;

  const theme = useMemo(
    () => ({
      colorScheme,
      fontFamily: FONT_FAMILY,
      primaryColor,
      // Mantine's defaults: shade 6 in light, 8 in dark (readable for the stock colours); the brand
      // and custom palettes are lifted in dark so their shade 8 still shows (see liftPalette).
      primaryShade: { light: 6, dark: 8 },
      focusRingStyles,
      defaultRadius: "md",
      colors: {
        brand: colorScheme === "dark" ? liftPalette(BRAND_PALETTE, 4) : BRAND_PALETTE,
        ...(customPalette
          ? { [CUSTOM_KEY]: colorScheme === "dark" ? liftPalette(customPalette, 2) : customPalette }
          : {}),
      },
      components: themeComponents,
      globalStyles,
    }),
    [colorScheme, primaryColor, customPalette],
  );

  const swatches = useMemo(() => {
    const presets = PRESET_KEYS.map((key) =>
      key === "brand"
        ? { key, label: brand.colorLabel || brand.name, color: BRAND_PALETTE[8] }
        : { key, label: PRESET_LABELS[key], color: DEFAULT_THEME.colors[key][6] },
    );
    return customHex
      ? [...presets, { key: CUSTOM_KEY, label: "Your colour", color: customHex }]
      : presets;
  }, [customHex]);

  const value = useMemo(
    () => ({
      colorScheme, // what the page shows (forced light on /login and /proposalpdf)
      preferredColorScheme: preferredScheme, // what the user saved
      forcedLight,
      setColorScheme,
      toggleColorScheme,
      primaryKey: primaryColor,
      setPrimaryKey,
      customHex,
      setCustomHex,
      swatches,
    }),
    [
      colorScheme,
      preferredScheme,
      forcedLight,
      setColorScheme,
      toggleColorScheme,
      primaryColor,
      setPrimaryKey,
      customHex,
      setCustomHex,
      swatches,
    ],
  );

  return (
    <AppearanceContext.Provider value={value}>
      <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
        <MantineProvider withGlobalStyles withNormalizeCSS theme={theme}>
          <DocumentTheme />
          {children}
        </MantineProvider>
      </ColorSchemeProvider>
    </AppearanceContext.Provider>
  );
}

/**
 * { colorScheme, preferredColorScheme, forcedLight, setColorScheme, toggleColorScheme,
 *   primaryKey, setPrimaryKey, customHex, setCustomHex, swatches: [{ key, label, color }] }
 */
export function useAppearance() {
  const value = useContext(AppearanceContext);
  if (!value) throw new Error("useAppearance() must be used inside <AppearanceProvider>.");
  return value;
}
